import styles from "./AccountMenu.module.css";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import ProfileIcon from "./ProfileIcon";
import Divider from "../common/Divider";
import SmallButton from "../common/SmallButton";
import {
  getLastUpdatedProfileImage,
  getMyTermsCountFromLocalStroage,
  getSlotsFromLocalStorage,
} from "../settings/util/userSettings";
import { formatNumberWithCommas } from "../common/strings";
import { auth } from "../../configurations/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { logout } from "./AuthManager";
import { useNavigate } from "react-router-dom";
import { profileImageUrl } from "../common/contants";
import { useAppSelector } from "../../hook";
import { selectLevel } from "../common/levelBadgeSlice";
import { getLevelEmoji } from "../common/levelBadge";
import { fetchIsSubscriber } from "../subscribe/service/subscribeAPI";
import { ReactComponent as SubscriberBadgeIcon } from "../../img/iconImgBadgeSubscriber.svg";
import Modal from "../../components/ui/Modal";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { useMyRankColor } from "./useMyRankColor";

interface AccountMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClickSlotAddButton: () => void;
  onClickCSVImportButton: () => void;
  onClickCSVExportButton: () => void;
  onClickSettingsButton: () => void;
  onClickLogOutButton: () => void;
  onClickContactUsButton: () => void;
  onClose: () => void;
}

export default function AccountMenu(props: AccountMenuProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const levelBadge = useAppSelector(selectLevel);
  const termsCount = formatNumberWithCommas(getMyTermsCountFromLocalStroage());
  const slotCount = formatNumberWithCommas(getSlotsFromLocalStorage());

  const { t } = useTranslation();
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({});
  const [profileIconSrc, setProfileIconSrc] = useState<string>();
  const [isSubscriber, setIsSubscriber] = useState(false);
  const rankColor = useMyRankColor();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setProfileIconSrc(
          `${profileImageUrl}${
            user.providerData[0].uid
          }.jpg?t=${getLastUpdatedProfileImage()}`
        );
      } else {
        logout();
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (props.open === false) {
      return;
    }

    let isCancelled = false;
    fetchIsSubscriber()
      .then((result) => {
        if (isCancelled === false) {
          setIsSubscriber(result);
        }
      })
      .catch(() => {
        if (isCancelled === false) {
          setIsSubscriber(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [props.open]);

  useEffect(() => {
    if (props.anchorEl && props.open) {
      const rect = props.anchorEl.getBoundingClientRect();
      const menuWidth = 280;
      const gap = 8;
      // 화면 아래쪽 버튼(사이드바 프로필)이면 위로, 오른쪽 끝이면 왼쪽으로 펼친다
      const opensUpward = rect.top > window.innerHeight / 2;
      const left =
        rect.left + menuWidth > window.innerWidth - gap
          ? rect.right - menuWidth
          : rect.left;
      setMenuStyles({
        width: `${menuWidth}px`,
        left: Math.max(gap, left),
        ...(opensUpward
          ? { bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap }),
      });
    }
  }, [props.anchorEl, props.open]);

  function handleOnClickContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.onClose();
  }

  function handleOnClickContentContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  function handleOnClickInstagram() {
    window.open("https://www.instagram.com/onevoca_kr/");
  }

  if (props.open === false || (!isMobile && props.anchorEl === null)) {
    return null;
  }

  const content = (
    <>
      <div className={styles.userInfoContainer}>
        <ProfileIcon
          size="40px"
          src={profileIconSrc}
          onCursorPoint={false}
          onClick={() => {}}
        />
        <div className={styles.userInfoTextContainer}>
          <p className={styles.usernameText}>
            <span style={{ color: rankColor }}>
              {auth.currentUser?.displayName}
            </span>{" "}
            {isSubscriber ? (
              <SubscriberBadgeIcon className={styles.subscriberBadge} />
            ) : (
              getLevelEmoji(levelBadge)
            )}
          </p>
          <p className={styles.userEmailText}>
            {auth.currentUser?.providerData[0].email}
          </p>
        </div>
      </div>
      <div className={styles.slotContainer}>
        <p className={styles.mySlotCountText}>
          <Trans i18nKey="MySlots" values={{ termsCount, slotCount }}>
            <span className={styles.mySlotCountTextHighlight}>
              {termsCount}
            </span>
            <span className={styles.mySlotCountTextHighlight}>{slotCount}</span>
          </Trans>
        </p>
        <SmallButton
          title={t("SlotAddButton")}
          variant="normal"
          onClick={() => props.onClickSlotAddButton()}
        />
      </div>
      <Divider />
      <div className={styles.buttonsContainer}>
        <div className={styles.featursContainer}>
          <button
            className={styles.button}
            onClick={() => props.onClickCSVImportButton()}
          >
            {t("CSVImportMenu")}
          </button>
        </div>
        <div className={styles.featursContainer}>
          <button
            className={styles.button}
            onClick={() => props.onClickCSVExportButton()}
          >
            {t("CSVExportMenu")}
          </button>
        </div>
      </div>
      <Divider />
      <div className={styles.buttonsContainer}>
        <div className={styles.featursContainer}>
          <button
            className={styles.button}
            onClick={() => props.onClickSettingsButton()}
          >
            {t("PersonalInformation")}
          </button>
        </div>
      </div>
      <Divider />
      <div className={styles.logOutContainer}>
        <button
          className={styles.button}
          onClick={() => props.onClickLogOutButton()}
        >
          {t("SignOut")}
        </button>
      </div>
      <Divider />
      <div className={styles.otherContainer}>
        <button
          className={styles.button}
          onClick={() => props.onClickContactUsButton()}
        >
          {t("ContactUs")}
        </button>
        <button
          className={styles.button}
          onClick={() => handleOnClickInstagram()}
        >
          {t("Instagram")}
        </button>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Modal open={props.open} onClose={props.onClose} placement="sheet">
        <div className={styles.sheetContent}>{content}</div>
      </Modal>
    );
  }

  return (
    <div className={styles.container} onClick={handleOnClickContainer}>
      <div
        className={styles.popover}
        style={menuStyles}
        onClick={handleOnClickContentContainer}
      >
        {content}
      </div>
    </div>
  );
}
